import { useTranslation } from "react-i18next";
import { HeartPulse, Users, Languages, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTAButtons } from "@/components/CTAButtons";
import { IndependenceStrip } from "@/components/IndependenceStrip";
import { CONTACT } from "@/data/site";

const VALUES = [
  { icon: HeartPulse, title: "Patient-first", text: "Your health, comfort and peace of mind guide every coordination decision." },
  { icon: ShieldCheck, title: "Independent providers", text: "We coordinate only with licensed independent doctors, nurses and facilities." },
  { icon: Languages, title: "Clear communication", text: "English-speaking coordinators keep everything simple and transparent." },
  { icon: Users, title: "Human support", text: "A real person helps you understand options and next steps." },
];

const About = () => {
  const { t } = useTranslation();
  return (
    <>
      <Seo
        title={t("about.seoTitle")}
        description={t("about.metaDescription")}
        path="/about"
        exact
      />
      <PageHero
        eyebrow={t("about.eyebrow")}
        title={t("about.h1")}
        subtitle={CONTACT.positioning}
        crumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.about") }]}
      />

      <section className="vc-container py-14 sm:py-16">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-[hsl(var(--vc-line))] bg-white p-10 vc-shadow-card">
              <img
                src="/brand/doctorsvietnam-logo.png"
                alt="Doctors Vietnam"
                className="mx-auto h-40 w-40 object-contain"
                loading="lazy"
              />
              <div className="mt-6 space-y-3 text-center">
                <p className="text-sm font-bold uppercase tracking-[0.14em] text-brand">DoctorsVietnam.com</p>
                <p className="text-sm text-[hsl(var(--vc-slate))]">English-speaking coordination for medical support in Vietnam.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl font-extrabold tracking-tight text-[hsl(var(--vc-ink))]">{t("about.oneTitle")}</h2>
            <p className="mt-4 text-base leading-relaxed text-[hsl(var(--vc-slate))]">{t("about.oneP1")}</p>
            <p className="mt-3 text-base leading-relaxed text-[hsl(var(--vc-slate))]">{t("about.oneP2")}</p>
            <CTAButtons className="mt-7" />
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.05}>
              <div className="h-full rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-mint text-brand"><v.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 text-lg font-bold text-[hsl(var(--vc-ink))]">{v.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12"><IndependenceStrip variant="band" /></Reveal>

        <Reveal className="mt-10">
          <div className="rounded-2xl border border-[hsl(var(--vc-line))] bg-white p-6 vc-shadow-soft" data-testid="supported-by-section">
            <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-brand">{t("about.supportedTitle")}</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[hsl(var(--vc-slate))]">
              {t("about.supportedText")}{" "}
              <a
                href="https://www.vietclinics.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-brand underline underline-offset-2"
                data-testid="about-vietclinics-link"
              >
                VietClinics.com <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </p>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default About;
